import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import {
    ArrowLeft,
    Edit3,
    Users,
    Calendar,
    Building2,
    Heart,
    TrendingUp,
    Clock,
    Shield,
    Truck,
    Target,
    Plus,
    X,
    Crown,
    UserPlus,
    Trash2,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Play,
    Pause,
    User,
    DollarSign,
    Bell,
    AlertCircle,
    Layers,
    NotebookPen
} from 'lucide-react';
import { capitalizeFirst, getStatusIcon } from "../common/utils";
import { updateTeam, getTeamDetails } from "../config/ApiConfig";
import { searchEmployees } from "../services/employeeDirectory";

const getOutcomeStatusIcon = (status) => {
    switch (status) {
        case 'Not Started':
            return <AlertTriangle className="w-4 h-4 text-gray-500" />;
        case 'In Progress':
            return <Play className="w-4 h-4 text-blue-500" />;
        case 'Blocked':
            return <XCircle className="w-4 h-4 text-red-500" />;
        case 'On Hold':
            return <Pause className="w-4 h-4 text-yellow-500" />;
        case 'Completed':
            return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
};

const getOutcomeStatusColor = (status) => {
    switch (status) {
        case 'Not Started':
            return 'bg-gray-100 text-gray-800';
        case 'In Progress':
            return 'bg-blue-100 text-blue-800';
        case 'Blocked':
            return 'bg-red-100 text-red-800';
        case 'On Hold':
            return 'bg-yellow-100 text-yellow-800';
        case 'Completed':
            return 'bg-green-100 text-green-800';
    }
};

const getTouchpointStatus = (lastDate, frequency) => {
    const lastTouchpoint = new Date(lastDate);
    const now = new Date();
    const daysDiff = Math.floor((now.getTime() - lastTouchpoint.getTime()) / (24 * 60 * 60 * 1000));
    const frequencyDays = parseInt(frequency) * 7;
    const warningDays = frequencyDays - 14; // 2 weeks before

    if (daysDiff >= frequencyDays) {
        return { status: 'immediate', label: 'Immediate Attention', color: 'bg-red-100 text-red-800', icon: AlertCircle };
    } else if (daysDiff >= warningDays) {
        return { status: 'warning', label: 'Due Soon', color: 'bg-yellow-100 text-yellow-800', icon: Bell };
    } else {
        return { status: 'good', label: 'On Track', color: 'bg-green-100 text-green-800', icon: CheckCircle };
    }
};

// Derive Product Development Health from last updated date
const getProductDevelopmentHealthFromDate = (lastUpdatedDate) => {
    if (!lastUpdatedDate) return 'At Risk';
    const last = new Date(lastUpdatedDate);
    if (isNaN(last.getTime())) return 'At Risk';
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - last.getTime()) / (24 * 60 * 60 * 1000));
    return diffDays <= 90 ? 'Healthy' : 'At Risk';
};

const outcomeStatusOptions = [
    "Not Started",
    "In Progress",
    "Blocked",
    "On Hold",
    "Completed",
];

const TeamDetailPageNew = () => {

    const { customerKey, teamKey } = useParams();
    const [team, setTeam] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showTeamModal, setShowTeamModal] = useState(false);
    const [showPerformanceModal, setShowPerformanceModal] = useState(false);
    const [showOutcomesModal, setShowOutcomesModal] = useState(false);
    const [showGrowthModal, setShowGrowthModal] = useState(false);
    const [showStakeholderModal, setShowStakeholderModal] = useState(false);
    const [editingOutcome, setEditingOutcome] = useState(null);
    const [editingGrowth, setEditingGrowth] = useState(null);
    const [editingStakeholder, setEditingStakeholder] = useState(null);
    const performanceMetrics = ['teamHealth', 'relationshipHealth', 'deliveryHealth'];
    const statuses = ['Start Strong', 'Healthy', 'At Risk', 'Critical'];
    const processAttributes = ['productLifecycle', 'cycleTime', 'businessPractices'];

    const [teamFormData, setTeamFormData] = useState({
        // customer: '',
        // businessUnit: '',
        // team: '',
        // startDate: '',
        // endDate: '',
        // members: []
    });

    const [performanceFormData, setPerformanceFormData] = useState({
        // teamHealth: '',
        // relationshipHealth: '',
        // deliveryHealth: '',
        // productLifecycle: '',
        // cycleTime: '',
        // businessPractices: ''
    });

    const [outcomeFormData, setOutcomeFormData] = useState({
        // shortDescription: '',
        // summary: '',
        // status: 'Not Started',
        // notes: ''
    });

    const [growthFormData, setGrowthFormData] = useState({
        // description: '',
        // priority: 'Medium',
        // targetDate: '',
        // owner: '',
        // status: 'Not Started'
    });

    const [stakeholderFormData, setStakeholderFormData] = useState({
        // name: '',
        // role: '',
        // organization: '',
        // email: '',
        // lastTouchpointDate: '',
        // touchpointFrequency: '6',
        // actionItems: [],
        // notes: ''
    });

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const data = await getTeamDetails(customerKey, teamKey);
                setTeam(data);
            } catch (error) {
                console.error('Error fetching team data:', error);
                setTeam(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [customerKey, teamKey]);

    const openTeamModal = () => {
        setTeamFormData({
            customer: team.customer,
            businessUnit: team.businessUnit || '',
            team: team.team,
            startDate: team.startDate?.split('T')[0],
            endDate: team.endDate?.split('T')[0] || '',
            renewalProbability: team.renewalProbability || '',
            members: team.members || []
        });
        setShowTeamModal(true);
    };

    const openPerformanceModal = () => {
        setPerformanceFormData({
            teamHealth: team.teamHealth || '',
            relationshipHealth: team.relationshipHealth || '',
            deliveryHealth: team.deliveryHealth || '',
            productLifecycle: team.productLifecycle || '',
            cycleTime: team.cycleTime || '',
            businessPractices: team.businessPractices || '',
            keynotes: team.keynotes || '',
            productDevelopmentHealthIndex: team.productDevelopmentHealthIndex || '',
            productDevelopmentHealthLastUpdated: team.productDevelopmentHealthLastUpdated || ''
        });
        setShowPerformanceModal(true);
    };

    const openOutcomesModal = (outcome = null) => {
        if (outcome) {
            setEditingOutcome(outcome);
            setOutcomeFormData({
                id: outcome.id,
                shortDescription: outcome.shortDescription,
                summary: outcome.summary,
                status: outcome.status,
                notes: outcome.notes
            });
        } else {
            setEditingOutcome(null);
            setOutcomeFormData({
                shortDescription: '',
                summary: '',
                status: 'Not Started',
                notes: ''
            });
        }
        setShowOutcomesModal(true);
    }

    const openGrowthModal = (growth = null) => {
        if (growth) {
            setEditingGrowth(growth);
            setGrowthFormData({
                id: growth.id,
                description: growth.description,
                priority: growth.priority,
                targetDate: growth.targetDate,
                owner: growth.owner,
                status: growth.status
            });
        } else {
            setEditingGrowth(null);
            setGrowthFormData({
                description: '',
                priority: 'Medium',
                targetDate: '',
                owner: '',
                status: 'Not Started'
            });
        };
        setShowGrowthModal(true);
    }

    const openStakeholderModal = (stakeholder = null) => {
        if (stakeholder) {
            setEditingStakeholder(stakeholder);
            setStakeholderFormData({
                id: stakeholder.id,
                name: stakeholder.name,
                role: stakeholder.role,
                customer: stakeholder.customer,
                email: stakeholder.email || '',
                lastTouchpointDate: stakeholder.lastTouchpointDate || '',
                touchpointFrequency: stakeholder.touchpointFrequency || '6',
                actionItems: stakeholder.actionItems,
                notes: stakeholder.notes || ''
            });
        } else {
            setEditingStakeholder(null);
            setStakeholderFormData({
                name: '',
                role: '',
                customer: team.customer || '',
                email: '',
                lastTouchpointDate: '',
                touchpointFrequency: '6',
                actionItems: [],
                notes: ''
            });
        }
        setShowStakeholderModal(true);
    }

    const handleTeamSubmit = async (e) => {
        e.preventDefault();
        const updatedTimestamp = new Date().toISOString();
        const toastId = toast.loading('Updating...');
        
        try {
            await updateTeam(teamFormData, team.customer, team.team);
            toast.success('Team details updated successfully!', { id: toastId });
            setTeam({ ...team, ...teamFormData, lastUpdated: updatedTimestamp });
            setShowTeamModal(false);
        } catch (error) {
            console.error('Error updating team:', error);
            toast.error('Team details update failed. Please try again.', { id: toastId });
        }
    };

    const handlePerformanceSubmit = async (e) => {
        e.preventDefault();
        const updatedTimestamp = new Date().toISOString();
        const toastId = toast.loading('Updating...');
        // derive and persist Product Development Health status
        const derivedProductDevHealth = getProductDevelopmentHealthFromDate(
            performanceFormData.productDevelopmentHealthLastUpdated
        );
        const payload = {
            ...performanceFormData,
            productDevelopmentHealth: derivedProductDevHealth
        };
        
        try {
            await updateTeam(payload, team.customer, team.team);
            toast.success('Performance metrics updated successfully!', { id: toastId });
            setTeam({ ...team, ...payload, lastUpdated: updatedTimestamp });
            setShowPerformanceModal(false);
        } catch (error) {
            console.error('Error updating performance metrics:', error);
            toast.error('Performance metrics update failed. Please try again.', { id: toastId });
        }
    };

    const handleOutcomeSubmit = async (e) => {
        e.preventDefault();
        const updatedTimestamp = new Date().toISOString();
        let outcomeData;
        if (editingOutcome) {
            outcomeData = team.outcomes.map(outcome =>
                outcome.id === editingOutcome.id
                    ? { ...outcome, ...outcomeFormData, updatedDate: updatedTimestamp }
                    : outcome
            );
        } else {
            const newOutcome = {
                ...outcomeFormData,
                id: `outcome-${Date.now()}`,
                createdDate: updatedTimestamp,
                updatedDate: updatedTimestamp
            };
            outcomeData = [newOutcome, ...(team.outcomes || [])];
        };
        
        const toastId = toast.loading('Updating...');
        
        try {
            await updateTeam({ outcomes: outcomeData }, team.customer, team.team);
            toast.success('Outcome updated successfully!', { id: toastId });
            setTeam({ ...team, outcomes: outcomeData, lastUpdated: updatedTimestamp });
            setShowOutcomesModal(false);
        } catch (error) {
            console.error('Error updating outcome:', error);
            toast.error('Outcome update failed. Please try again.', { id: toastId });
        }
    };

    const handleGrowthSubmit = async (e) => {
        e.preventDefault();
        const updatedTimestamp = new Date().toISOString();
        let growthData;
        if (editingGrowth) {
            growthData = team.growthOpportunities.map(metric =>
                metric.id === editingGrowth.id
                    ? { ...metric, ...growthFormData, updatedDate: updatedTimestamp }
                    : metric
            );
        } else {
            const newGrowthMetric = {
                ...growthFormData,
                id: `growth-${Date.now()}`,
                createdDate: updatedTimestamp,
                updatedDate: updatedTimestamp
            };
            growthData = [newGrowthMetric, ...(team.growthOpportunities || [])];
        }
        
        const toastId = toast.loading('Updating...');
        
        try {
            await updateTeam({ growthOpportunities: growthData }, team.customer, team.team);
            toast.success('Growth opportunities updated successfully!', { id: toastId });
            setTeam({ ...team, growthOpportunities: growthData, lastUpdated: updatedTimestamp });
            setShowGrowthModal(false);
        } catch (error) {
            console.error('Error updating growth opportunities:', error);
            toast.error('Growth opportunities update failed. Please try again.', { id: toastId });
        }
    };

    const handleStakeholderSubmit = async (e) => {
        e.preventDefault();
        const updatedTimestamp = new Date().toISOString();
        let stakeholderData;
        if (editingStakeholder) {
            stakeholderData = team.stakeholders.map(stakeholder =>
                stakeholder.id === editingStakeholder.id
                    ? { ...stakeholder, ...stakeholderFormData }
                    : stakeholder
            );
        } else {
            const newStakeholder = {
                ...stakeholderFormData,
                id: `stakeholder-${Date.now()}`
            };
            stakeholderData = [newStakeholder, ...(team.stakeholders || [])];
        }
        
        const toastId = toast.loading('Updating...');
        
        try {
            await updateTeam({ stakeholders: stakeholderData }, team.customer, team.team);
            toast.success('Stakeholder updated successfully!', { id: toastId });
            setTeam({ ...team, stakeholders: stakeholderData, lastUpdated: updatedTimestamp });
            setShowStakeholderModal(false);
        } catch (error) {
            console.error('Error updating stakeholder:', error);
            toast.error('Stakeholder update failed. Please try again.', { id: toastId });
        }
    };

    const addTeamMember = () => {
        const newMember = {
            id: `member-${Date.now()}`,
            name: '',
            role: '',
            employeeId: '',
            isCaptain: false,
            isInvestment: false
        };
        setTeamFormData(prev => ({
            ...prev,
            members: [...prev.members, newMember]
        }));
    };

    const updateTeamMember = (index, field, value) => {
        setTeamFormData(prev => ({
            ...prev,
            members: prev.members.map((member, i) =>
                i === index ? { ...member, [field]: value } : member
            )
        }));
    };

    const removeTeamMember = (index) => {
        setTeamFormData(prev => ({
            ...prev,
            members: prev.members.filter((_, i) => i !== index)
        }));
    };

    const setCaptain = (memberId) => {
        setTeamFormData(prev => ({
            ...prev,
            members: prev.members.map(member => ({
                ...member,
                isCaptain: member.id === memberId ? !member.isCaptain : member.isCaptain
            }))
        }));
    };

    const addActionItem = () => {
        setStakeholderFormData(prev => ({
            ...prev,
            actionItems: [...prev.actionItems, '']
        }));
    };

    const updateActionItem = (index, value) => {
        setStakeholderFormData(prev => ({
            ...prev,
            actionItems: prev.actionItems.map((item, i) => i === index ? value : item)
        }));
    };

    const removeActionItem = (index) => {
        setStakeholderFormData(prev => ({
            ...prev,
            actionItems: prev.actionItems.filter((_, i) => i !== index)
        }));
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            {isLoading ? (
                <div className="flex flex-col items-center justify-center h-screen">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-solid" />
                    <p className="mt-4 text-gray-600">Loading data...</p>
                </div>
            ) : !team ? (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-gray-500 text-lg">Team not found</div>
                        <Link to="/" className="text-blue-600 hover:text-blue-800 mt-2 inline-block">
                            Return to Dashboard
                        </Link>
                    </div>
                </div>
            ) : (
                <div className="max-w-7xl mx-auto px-4">
                    <Link to="/teams" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6">
                        <ArrowLeft className="w-5 h-5 mr-1" />
                        Back to Teams
                    </Link>

                    <div className="flex justify-between items-center mb-8">
                        <h1 className="text-3xl font-bold text-gray-900">{team.team}</h1>
                        <div className="text-sm text-gray-500">
                            Last updated: {team.lastUpdated ? new Date(team.lastUpdated).toLocaleString('en-GB', {
                                year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true
                            }).replace(',', '') : 'Never'}
                        </div>
                    </div>

                    {/* Team Details Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                                    <Users className="w-6 h-6 mr-2 text-blue-500" />
                                    Team Details
                                </h2>
                                <button
                                    onClick={() => openTeamModal()}
                                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    <Edit3 className="w-4 h-4 mr-2" />
                                    Edit
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Customer</label>
                                    <div className="flex items-center">
                                        <Building2 className="w-5 h-5 text-gray-400 mr-2" />
                                        <span className="text-gray-900">{team.customer}</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Business Unit</label>
                                    <div className="flex items-center">
                                        <Layers className="w-5 h-5 text-gray-400 mr-2" />
                                        <span className="text-gray-900">{team.businessUnit}</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Start Date</label>
                                    <div className="flex items-center">
                                        <Calendar className="w-5 h-5 text-gray-400 mr-2" />
                                        <span className="text-gray-900">{new Date(team.startDate).toLocaleDateString()}</span>
                                    </div>
                                </div>
                                {team.endDate && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500 mb-1">End Date</label>
                                        <div className="flex items-center">
                                            <Calendar className="w-5 h-5 text-gray-400 mr-2" />
                                            <span className="text-gray-900">{new Date(team.endDate).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Renewal Probability</label>
                                    <div className="flex items-center">
                                        <span className="text-gray-900">
                                            {team.renewalProbability !== undefined && team.renewalProbability !== null && team.renewalProbability !== '' 
                                                ? `${team.renewalProbability}%` 
                                                : '-'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6">
                                <label className="block text-sm font-medium text-gray-500 mb-3">Team Members</label>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {team.members?.sort((a, b) => (b.isCaptain - a.isCaptain) || a.id.localeCompare(b.id))
                                        .map(member => {
                                            const hasInvestmentDetails = member.isInvestment && member.investmentStartDate && member.investmentDuration;
                                            let investmentPeriodText = '';
                                            if (hasInvestmentDetails) {
                                                const start = new Date(member.investmentStartDate);
                                                const end = new Date(start);
                                                end.setMonth(end.getMonth() + parseInt(member.investmentDuration, 10));

                                                const options = { year: 'numeric', month: 'short' };
                                                const startStr = start.toLocaleDateString(undefined, options);
                                                const endStr = end.toLocaleDateString(undefined, options);
                                                investmentPeriodText = `(${startStr} – ${endStr})`;
                                            }

                                            return (
                                                <div key={member.id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                                                    <div className="flex-1 ml-3">
                                                        <div className="flex items-center">
                                                            <span className="font-medium text-gray-900">{member.name}</span>
                                                            {member.isCaptain && <Crown className="w-4 h-4 text-yellow-500 ml-1" />}
                                                            {member.isInvestment && <DollarSign className="w-4 h-4 text-green-500 ml-1" />}
                                                        </div>

                                                        <div className="text-sm text-gray-500 flex space-x-3">
                                                            <span>{member.role}</span>
                                                            {investmentPeriodText && (
                                                                <span className="text-gray-400">{investmentPeriodText}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Performance Index Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                                    <TrendingUp className="w-6 h-6 mr-2 text-green-500" />
                                    Performance Index
                                </h2>
                                <button
                                    onClick={() => openPerformanceModal()}
                                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    <Edit3 className="w-4 h-4 mr-2" />
                                    Edit
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                                <div className="flex items-start space-x-3">
                                    <Heart className="w-6 h-6 text-red-500 mt-1" />
                                    <div>
                                        <h3 className="font-medium text-gray-900">Team Health</h3>
                                        <div className="flex items-center mt-2">
                                            {getStatusIcon(team.teamHealth)}
                                            <span className="ml-2 text-sm text-gray-700">{team.teamHealth || 'N/A'}</span>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-2">Team morale and collaboration</p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-3">
                                    <Users className="w-6 h-6 text-blue-500 mt-1" />
                                    <div>
                                        <h3 className="font-medium text-gray-900">Relationship Health</h3>
                                        <div className="flex items-center mt-2">
                                            {getStatusIcon(team.relationshipHealth)}
                                            <span className="ml-2 text-sm text-gray-700">{team.relationshipHealth || 'N/A'}</span>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-2">Stakeholder relationships</p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-3">
                                    <Truck className="w-6 h-6 text-purple-500 mt-1" />
                                    <div>
                                        <h3 className="font-medium text-gray-900">Delivery Health</h3>
                                        <div className="flex items-center mt-2">
                                            {getStatusIcon(team.deliveryHealth)}
                                            <span className="ml-2 text-sm text-gray-700">{team.deliveryHealth || 'N/A'}</span>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-2">Delivery performance</p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-3">
                                    <TrendingUp className="w-6 h-6 text-green-500 mt-1" />
                                    <div>
                                        <h3 className="font-medium text-gray-900">Product Lifecycle</h3>
                                        <div className="flex items-center mt-2">
                                            {/* {getStatusIcon('Healthy')} */}
                                            <span className="text-sm text-gray-700">{team.productLifecycle || '-'}</span>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-2">Product growth and maturity</p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-3">
                                    <Clock className="w-6 h-6 text-orange-500 mt-1" />
                                    <div>
                                        <h3 className="font-medium text-gray-900">Cycle Time</h3>
                                        <div className="flex items-center mt-2">
                                            {/* {getStatusIcon('At Risk')} */}
                                            <span className="text-sm text-gray-700">{team.cycleTime || '-'}</span>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-2">Development cycle efficiency</p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-3">
                                    <Shield className="w-6 h-6 text-indigo-500 mt-1" />
                                    <div>
                                        <h3 className="font-medium text-gray-900">Business Practices</h3>
                                        <div className="flex items-center mt-2">
                                            {/* {getStatusIcon('Healthy')} */}
                                            <span className="text-sm text-gray-700">{team.businessPractices || '-'}</span>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-2">Process compliance and quality</p>
                                    </div>
                                </div>
                            </div>
                            {/* Product Development Health Section */}
                            <div className="mt-10 pt-6 border-t border-gray-200">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <Layers className="w-6 h-6 text-teal-600 mt-1" />
                                    </div>
                                    <div className="ml-3 flex-1">
                                        <h3 className="font-medium text-gray-900">Product Development Health Index</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-3">
                                            <div>
                                                <div className="flex items-center">
                                                    {getStatusIcon(getProductDevelopmentHealthFromDate(team.productDevelopmentHealthLastUpdated))}
                                                    <span className="ml-2 text-sm text-gray-700">{getProductDevelopmentHealthFromDate(team.productDevelopmentHealthLastUpdated)}</span>
                                                </div>
                                                <div className="mt-3">
                                                    {team.productDevelopmentHealthIndex ? (
                                                        <a href={team.productDevelopmentHealthIndex} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all text-sm">{team.productDevelopmentHealthIndex}</a>
                                                    ) : (
                                                        <span className="text-sm text-gray-500">No document attached yet</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">Last Updated Date</label>
                                                <div className="mt-2">
                                                    {team.productDevelopmentHealthLastUpdated ? (
                                                        <span className="text-sm text-gray-700">{new Date(team.productDevelopmentHealthLastUpdated).toLocaleDateString()}</span>
                                                    ) : (
                                                        <span className="text-sm text-gray-700">-</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-10 pt-6 border-t border-gray-200">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <NotebookPen className="w-6 h-6 text-orange-400 mt-1" />
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="font-medium text-gray-900">Key Notes and Risk</h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-700 whitespace-pre-line">
                                                {team.keynotes || '-'}
                                            </p>
                                        </div>
                                        <p className="text-sm text-gray-500 mt-2">Important observations and potential risks</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Outcomes Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                                    <Target className="w-6 h-6 mr-2 text-purple-500" />
                                    Outcomes
                                </h2>
                                <button
                                    onClick={() => openOutcomesModal()}
                                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                {(!team.outcomes || team.outcomes?.length === 0) ? <p>No outcomes available</p> : team.outcomes?.map(outcome => (
                                    <div key={outcome.id} className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-2 mb-2">
                                                    {getOutcomeStatusIcon(outcome.status)}
                                                    <h3 className="font-medium text-gray-900">{outcome.shortDescription}</h3>
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getOutcomeStatusColor(outcome.status)}`}>
                                                        {outcome.status}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-600 mb-2">{outcome.summary}</p>
                                                {outcome.notes && (
                                                    <p className="text-sm text-gray-500 italic">{outcome.notes}</p>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => openOutcomesModal(outcome)}
                                                className="ml-4 text-blue-600 hover:text-blue-800"
                                            >
                                                <Edit3 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="text-xs text-gray-400 mt-2">
                                            Created: {new Date(outcome.createdDate).toLocaleDateString()}
                                        </div>
                                        <div className="text-xs text-gray-400 mt-2">
                                            Last updated: {new Date(outcome.updatedDate).toLocaleDateString()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Growth Opportunities Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                                    <TrendingUp className="w-6 h-6 mr-2 text-green-500" />
                                    Growth Opportunities
                                </h2>
                                <button
                                    onClick={() => openGrowthModal()}
                                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                {(!team.growthOpportunities || team.growthOpportunities?.length === 0) ? <p>No growth opportunities available</p> : team.growthOpportunities?.map(opportunity => (
                                    <div key={opportunity.id} className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-2 mb-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${opportunity.priority === 'High' ? 'bg-red-100 text-red-800' :
                                                        opportunity.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                                            'bg-green-100 text-green-800'
                                                        }`}>
                                                        {opportunity.priority}
                                                    </span>
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${opportunity.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                                                        opportunity.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                                            'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {opportunity.status}
                                                    </span>
                                                </div>
                                                <p className="text-gray-900 mb-2">{opportunity.description}</p>
                                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                                    {opportunity.targetDate && (
                                                        <div className="flex items-center">
                                                            <Calendar className="w-4 h-4 mr-1" />
                                                            Target: {new Date(opportunity.targetDate).toLocaleDateString()}
                                                        </div>
                                                    )}
                                                    {opportunity.owner && (
                                                        <div className="flex items-center">
                                                            <User className="w-4 h-4 mr-1" />
                                                            {opportunity.owner}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => openGrowthModal(opportunity)}
                                                className="ml-4 text-blue-600 hover:text-blue-800"
                                            >
                                                <Edit3 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Stakeholders and Touchpoints Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                                    <Users className="w-6 h-6 mr-2 text-blue-500" />
                                    Stakeholders & Touchpoints
                                </h2>
                                <button
                                    onClick={() => openStakeholderModal()}
                                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {(!team.stakeholders || team.stakeholders?.length === 0) ? <p>No stakeholders found</p> : team.stakeholders.map(stakeholder => {
                                    const touchpointStatus = stakeholder.lastTouchpointDate
                                        ? getTouchpointStatus(stakeholder.lastTouchpointDate, stakeholder.touchpointFrequency)
                                        : { status: 'immediate', label: 'No Contact', color: 'bg-gray-100 text-gray-800', icon: AlertCircle };

                                    return (
                                        <div key={stakeholder.id} className="border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex-1">
                                                    <h3 className="font-medium text-gray-900">{stakeholder.name}</h3>
                                                    <p className="text-sm text-gray-600">{stakeholder.role}</p>
                                                    {/* <p className="text-xs text-gray-500">{stakeholder.customer}</p> */}
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => openStakeholderModal(stakeholder)}
                                                        className="text-blue-600 hover:text-blue-800"
                                                    >
                                                        <Edit3 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${touchpointStatus.color}`}>
                                                    <touchpointStatus.icon className="w-3 h-3 mr-1" />
                                                    {touchpointStatus.label}
                                                </div>
                                            </div>

                                            {stakeholder.lastTouchpointDate && (
                                                <div className="text-sm text-gray-500 mb-3">
                                                    Last contact: {new Date(stakeholder.lastTouchpointDate).toLocaleDateString()}
                                                </div>
                                            )}

                                            {stakeholder.actionItems.length > 0 && (
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-700 mb-1">Action Items:</h4>
                                                    <ul className="text-sm text-gray-600 space-y-1">
                                                        {stakeholder.actionItems.map((item, index) => (
                                                            <li key={index} className="flex items-start">
                                                                <span className="w-1 h-1 bg-gray-400 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                                                                {item}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Team Details Modal */}
            {showTeamModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Edit Team Details</h2>
                            <button
                                onClick={() => setShowTeamModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleTeamSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Customer Name
                                    </label>
                                    <CreatableSelect
                                        value={teamFormData.customer ? { label: teamFormData.customer, value: teamFormData.customer } : null}
                                        isDisabled={true}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Business Unit
                                    </label>
                                    <CreatableSelect
                                        value={teamFormData.businessUnit ? { label: teamFormData.businessUnit, value: teamFormData.businessUnit } : null}
                                        isDisabled={true}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Team Name
                                    </label>
                                    <input
                                        type="text"
                                        value={teamFormData.team}
                                        onChange={(e) => setTeamFormData(prev => ({ ...prev, team: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-400"
                                        disabled
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        value={teamFormData.startDate}
                                        onChange={(e) => setTeamFormData(prev => ({ ...prev, startDate: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        End Date (Optional)
                                    </label>
                                    <input
                                        type="date"
                                        value={teamFormData.endDate}
                                        onChange={(e) => setTeamFormData(prev => ({ ...prev, endDate: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Renewal Probability
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="1"
                                            value={teamFormData.renewalProbability || ''}
                                            onChange={(e) => setTeamFormData(prev => ({ ...prev, renewalProbability: e.target.value }))}
                                            className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Enter renewal probability"
                                        />
                                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">%</span>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-4 border-t pt-6">
                                    <h3 className="text-lg font-medium text-gray-900">Team Members</h3>
                                    <button
                                        type="button"
                                        onClick={addTeamMember}
                                        className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                                    >
                                        <UserPlus className="w-4 h-4 mr-2" />
                                        Add Member
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {teamFormData.members.map((member, index) => (
                                        <div
                                            key={member.id}
                                            className="flex flex-col space-y-2 p-4 border border-gray-200 rounded-lg"
                                        >
                                            <div className="flex items-center space-x-4">
                                                <div className="flex-1">
                                                    <AsyncSelect
                                                        cacheOptions
                                                        defaultOptions
                                                        isClearable
                                                        placeholder="Name (type to search)"
                                                        noOptionsMessage={() => "No employees found"}
                                                        loadOptions={async (inputValue) => {
                                                            try {
                                                                const results = await searchEmployees(inputValue);
                                                                return results.map(e => ({
                                                                    value: e.id,
                                                                    label: e.name + (e.employeeId ? ` (${e.employeeId})` : ''),
                                                                    meta: e
                                                                }));
                                                            } catch (err) {
                                                                // Fallback to plain input if Excel missing
                                                                return [];
                                                            }
                                                        }}
                                                        onChange={(opt) => {
                                                            if (opt && opt.meta) {
                                                                updateTeamMember(index, 'name', capitalizeFirst(opt.meta.name));
                                                                updateTeamMember(index, 'employeeId', opt.meta.employeeId || opt.meta.id);
                                                            } else {
                                                                updateTeamMember(index, 'name', '');
                                                                updateTeamMember(index, 'employeeId', '');
                                                            }
                                                        }}
                                                        value={member.name ? { value: member.employeeId || member.name, label: member.name, meta: null } : null}
                                                        className="w-full"
                                                    />
                                                </div>
                                                <input
                                                    type="text"
                                                    placeholder="Role"
                                                    value={member.role}
                                                    onChange={(e) => updateTeamMember(index, 'role', capitalizeFirst(e.target.value))}
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    required
                                                />
                                                <label className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={member.isInvestment || false}
                                                        onChange={(e) => updateTeamMember(index, 'isInvestment', e.target.checked)}
                                                        className="mr-2"
                                                    />
                                                    <DollarSign className="w-4 h-4 text-green-500 mr-1" />
                                                    <span className="text-sm text-gray-700">Investment</span>
                                                </label>
                                                <button
                                                    type="button"
                                                    onClick={() => setCaptain(member.id)}
                                                    className={`px-3 py-2 rounded-md flex items-center ${member.isCaptain === true
                                                        ? 'bg-yellow-100 text-yellow-800'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        }`}
                                                >
                                                    <Crown className="w-4 h-4 mr-1" />
                                                    Captain
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => removeTeamMember(index)}
                                                    className="px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>

                                            {member.isInvestment && (
                                                <div className="w-full bg-gray-50 p-3 rounded-md border border-gray-200">
                                                    <div className="flex space-x-4">
                                                        <div className="flex-1">
                                                            <label className="block text-sm text-gray-600 mb-1">Start Date</label>
                                                            <input
                                                                type="date"
                                                                value={member.investmentStartDate || ''}
                                                                onChange={(e) => updateTeamMember(index, 'investmentStartDate', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                required
                                                            />
                                                        </div>
                                                        <div className="flex-1">
                                                            <label className="block text-sm text-gray-600 mb-1">Duration (in months)</label>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                step="1"
                                                                value={member.investmentDuration || ''}
                                                                onChange={(e) => updateTeamMember(index, 'investmentDuration', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-end space-x-4 pt-8">
                                <button
                                    type="button"
                                    onClick={() => setShowTeamModal(false)}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Update Team
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Performance Index Modal */}
            {showPerformanceModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Edit Performance Metrics</h2>
                            <button
                                onClick={() => setShowPerformanceModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handlePerformanceSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {performanceMetrics.map(
                                    (metric) => (
                                        <div key={metric}>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {metric.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            </label>
                                            <Select
                                                options={statuses.map(status => ({ label: status, value: status }))}
                                                value={performanceFormData[metric] ? { label: performanceFormData[metric], value: performanceFormData[metric] } : null}
                                                onChange={(selected) => setPerformanceFormData(prev => ({ ...prev, [metric]: selected ? selected.value : '' }))}
                                            />
                                        </div>
                                    )
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {processAttributes.map(
                                    (attribute) => (
                                        <div key={attribute}>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {attribute.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            </label>
                                            <input
                                                type="text"
                                                value={performanceFormData[attribute]}
                                                onChange={(e) => setPerformanceFormData(prev => ({ ...prev, [attribute]: capitalizeFirst(e.target.value) }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    )
                                )}
                            </div>
                            {/* Product Development Health Section */}
                            <div className="pt-6 border-t border-gray-200">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Product Development Health Index</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <input
                                            type="url"
                                            placeholder="Add your link here (https://example.com)"
                                            value={performanceFormData.productDevelopmentHealthIndex || ''}
                                            onChange={(e) => setPerformanceFormData(prev => ({
                                                ...prev,
                                                productDevelopmentHealthIndex: e.target.value
                                            }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="sr-only">
                                            Last Updated Date
                                        </label>
                                        <input
                                            type="date"
                                            value={performanceFormData.productDevelopmentHealthLastUpdated || ''}
                                            onChange={(e) => setPerformanceFormData(prev => ({ ...prev, productDevelopmentHealthLastUpdated: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                                <div className="mt-6 border-t border-gray-200"></div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Key Notes and Risk
                                </label>
                                <textarea
                                    value={performanceFormData.keynotes}
                                    onChange={(e) => setPerformanceFormData(prev => ({ ...prev, keynotes: capitalizeFirst(e.target.value) }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    rows={5}
                                />
                            </div>

                            <div className="flex justify-end space-x-4">
                                <button
                                    type="button"
                                    onClick={() => setShowPerformanceModal(false)}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Update Team
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Outcomes Management Modal */}
            {showOutcomesModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {editingOutcome ? 'Edit Outcome' : 'Add New Outcome'}
                            </h2>
                            <button
                                onClick={() => setShowOutcomesModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleOutcomeSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Short Description
                                </label>
                                <input
                                    type="text"
                                    value={outcomeFormData.shortDescription}
                                    onChange={(e) => setOutcomeFormData(prev => ({ ...prev, shortDescription: capitalizeFirst(e.target.value) }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Summary
                                </label>
                                <textarea
                                    value={outcomeFormData.summary}
                                    onChange={(e) => setOutcomeFormData(prev => ({ ...prev, summary: capitalizeFirst(e.target.value) }))}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status
                                </label>
                                <Select
                                    options={outcomeStatusOptions.map(status => ({ label: status, value: status }))}
                                    value={outcomeFormData.status ? { label: outcomeFormData.status, value: outcomeFormData.status } : null}
                                    onChange={(selected) => setOutcomeFormData(prev => ({ ...prev, status: selected ? selected.value : 'Not Started' }))}
                                    className="w-full"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Notes
                                </label>
                                <textarea
                                    value={outcomeFormData.notes}
                                    onChange={(e) => setOutcomeFormData(prev => ({ ...prev, notes: capitalizeFirst(e.target.value) }))}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div className="flex justify-end space-x-4 pt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowOutcomesModal(false)}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    {editingOutcome ? 'Update Outcome' : 'Add Outcome'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Growth Opportunities Modal */}
            {showGrowthModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {editingGrowth ? 'Edit Growth Opportunity' : 'Add Growth Opportunity'}
                            </h2>
                            <button
                                onClick={() => setShowGrowthModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleGrowthSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={growthFormData.description}
                                    onChange={(e) =>
                                        setGrowthFormData({
                                            ...growthFormData,
                                            description: capitalizeFirst(e.target.value),
                                        })
                                    }
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Priority
                                    </label>
                                    <Select
                                        options={[
                                            { label: 'High', value: 'High' },
                                            { label: 'Medium', value: 'Medium' },
                                            { label: 'Low', value: 'Low' }
                                        ]}
                                        value={growthFormData.priority ? { label: growthFormData.priority, value: growthFormData.priority } : null}
                                        onChange={(selected) => setGrowthFormData(prev => ({ ...prev, priority: selected ? selected.value : 'Medium' }))}
                                        className="w-full"
                                    />

                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Status
                                    </label>
                                    <Select
                                        options={[
                                            { label: 'Not Started', value: 'Not Started' },
                                            { label: 'In Progress', value: 'In Progress' },
                                            { label: 'Completed', value: 'Completed' }
                                        ]}
                                        value={growthFormData.status ? { label: growthFormData.status, value: growthFormData.status } : null}
                                        onChange={(selected) => setGrowthFormData(prev => ({ ...prev, status: selected ? selected.value : 'Not Started' }))}
                                        className="w-full"
                                    />

                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Target Date
                                    </label>
                                    <input
                                        type="date"
                                        value={growthFormData.targetDate}
                                        onChange={(e) => setGrowthFormData(prev => ({ ...prev, targetDate: e.target.value }))}
                                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Owner
                                </label>
                                <input
                                    type="text"
                                    value={growthFormData.owner}
                                    onChange={(e) => setGrowthFormData(prev => ({ ...prev, owner: capitalizeFirst(e.target.value) }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter owner name"
                                    required
                                />
                            </div>

                            <div className="flex justify-end space-x-4 pt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowGrowthModal(false)}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    {editingGrowth ? 'Update Growth Opportunity' : 'Add Growth Opportunity'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Stakeholder Modal */}
            {showStakeholderModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {editingStakeholder ? 'Edit Stakeholder' : 'Add New Stakeholder'}
                            </h2>
                            <button
                                onClick={() => setShowStakeholderModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleStakeholderSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Name
                                    </label>
                                    <input
                                        type="text"
                                        value={stakeholderFormData.name}
                                        onChange={(e) => setStakeholderFormData(prev => ({ ...prev, name: capitalizeFirst(e.target.value) }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Role
                                    </label>
                                    <input
                                        type="text"
                                        value={stakeholderFormData.role}
                                        onChange={(e) => setStakeholderFormData(prev => ({ ...prev, role: capitalizeFirst(e.target.value) }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Customer
                                    </label>
                                    <Select
                                        value={team.customer ? { label: team.customer, value: team.customer } : null}
                                        isDisabled={true}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={stakeholderFormData.email}
                                        onChange={(e) => setStakeholderFormData(prev => ({ ...prev, email: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Last Touchpoint Date
                                    </label>
                                    <input
                                        type="date"
                                        value={stakeholderFormData.lastTouchpointDate}
                                        onChange={(e) => setStakeholderFormData(prev => ({ ...prev, lastTouchpointDate: e.target.value }))}
                                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Touchpoint Frequency
                                    </label>
                                    <Select
                                        options={[
                                            { label: 'Every 4 weeks', value: '4' },
                                            { label: 'Every 6 weeks', value: '6' },
                                            { label: 'Every 12 weeks', value: '12' }
                                        ]}
                                        value={stakeholderFormData.touchpointFrequency ? { label: `Every ${stakeholderFormData.touchpointFrequency} weeks`, value: stakeholderFormData.touchpointFrequency } : null}
                                        onChange={(selected) => setStakeholderFormData(prev => ({ ...prev, touchpointFrequency: selected ? selected.value : '4' }))}
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            <div>
                                <div className="flex justify-between items-center mt-6 mb-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Action Items
                                    </label>
                                    <button
                                        type="button"
                                        onClick={addActionItem}
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                    >
                                        + Add Item
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {stakeholderFormData.actionItems.map((item, index) => (
                                        <div key={index} className="flex items-center space-x-2">
                                            <input
                                                type="text"
                                                value={item}
                                                onChange={(e) => updateActionItem(index, capitalizeFirst(e.target.value))}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Action item..."
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeActionItem(index)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Notes
                                </label>
                                <textarea
                                    value={stakeholderFormData.notes}
                                    onChange={(e) => setStakeholderFormData(prev => ({ ...prev, notes: capitalizeFirst(e.target.value) }))}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div className="flex justify-end space-x-4">
                                <button
                                    type="button"
                                    onClick={() => setShowStakeholderModal(false)}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    {editingStakeholder ? 'Update Stakeholder' : 'Add Stakeholder'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamDetailPageNew;